import { useCallback, useEffect } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_ORDERS_TABLE } from "../../constants/FirestoreConstant";
import { ORDER_STATUS_CREATED } from "../../constants/OrderConstant";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import * as dayjs from "dayjs";

/**
 * Fetcher function for orders - fetches from both databases
 */
const fetchOrdersFromFirestore = async (deliveryDate) => {
  console.log("[useOrders] 🔍 Fetching orders for delivery date:", deliveryDate);
  console.log("[useOrders] 📋 Using collection:", FIRESTORE_ORDERS_TABLE);
  console.log("[useOrders] ⚙️ Filtering out status:", ORDER_STATUS_CREATED);

  // Check auth state
  const vceAuth = vceFirestore.app.auth();
  const hvoAuth = hvoFirestore.app.auth();
  console.log("[useOrders] 🟢 VCE auth user:", vceAuth.currentUser || "NOT LOGGED IN");
  console.log("[useOrders] 🟣 HVO auth user:", hvoAuth.currentUser || "NOT LOGGED IN");

  // Fetch VCE orders
  console.log("[useOrders] 🟢 Fetching VCE orders...");
  let vceOrdersSnapshot;
  try {
    vceOrdersSnapshot = await vceFirestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .where("delivery.date", "==", deliveryDate)
      .where("orderStatus", "!=", ORDER_STATUS_CREATED)
      .get();
    console.log("[useOrders] 🟢 VCE snapshot size:", vceOrdersSnapshot.size);
    console.log("[useOrders] 🟢 VCE empty:", vceOrdersSnapshot.empty);
  } catch (error) {
    console.error("[useOrders] ❌ VCE query failed:", error);
    console.error("[useOrders] ❌ Error code:", error.code);
    console.error("[useOrders] ❌ Error message:", error.message);
    // Return empty snapshot equivalent
    vceOrdersSnapshot = { docs: [], size: 0, empty: true };
  }

  const vceOrders = vceOrdersSnapshot.docs.map((doc) => {
    const data = doc.data();
    console.log("[useOrders] 🟢 VCE Order:", {
      id: doc.id,
      orderId: data.orderId,
      deliveryDate: data.delivery?.date,
      orderStatus: data.orderStatus,
    });
    return {
      id: doc.id,
      ...data,
      shopSource: "VCE",
      shopName: "Vleescentrum",
    };
  });

  // Fetch HVO orders
  console.log("[useOrders] 🟣 Fetching HVO orders...");
  let hvoOrdersSnapshot;
  try {
    hvoOrdersSnapshot = await hvoFirestore
      .collection(FIRESTORE_ORDERS_TABLE)
      .where("delivery.date", "==", deliveryDate)
      .where("orderStatus", "!=", ORDER_STATUS_CREATED)
      .get();
    console.log("[useOrders] 🟣 HVO snapshot size:", hvoOrdersSnapshot.size);
    console.log("[useOrders] 🟣 HVO empty:", hvoOrdersSnapshot.empty);
  } catch (error) {
    console.error("[useOrders] ❌ HVO query failed:", error);
    console.error("[useOrders] ❌ Error code:", error.code);
    console.error("[useOrders] ❌ Error message:", error.message);
    // Return empty snapshot equivalent
    hvoOrdersSnapshot = { docs: [], size: 0, empty: true };
  }

  const hvoOrders = hvoOrdersSnapshot.docs
    .filter((doc) => !doc.data().isVleescentrum) // Filter out VCE orders
    .map((doc) => {
      const data = doc.data();
      console.log("[useOrders] 🟣 HVO Order:", {
        id: doc.id,
        orderId: data.orderId,
        deliveryDate: data.delivery?.date,
        orderStatus: data.orderStatus,
      });
      return {
        id: doc.id,
        ...data,
        shopSource: "HVO",
        shopName: "Halalvlees Online",
      };
    });

  // Combine and sort by creation date (newest first)
  const combinedOrders = [...vceOrders, ...hvoOrders].sort((a, b) => b.createdAt - a.createdAt);
  console.log("[useOrders] ✅ Total orders returned:", combinedOrders.length);

  return combinedOrders;
};

/**
 * Hook to fetch and manage orders from both VCE and HVO Firestore databases
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.deliveryDate - Filter orders by delivery date (DD-MM-YYYY format, default: today)
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 30000 for realtime, 0 otherwise)
 * @returns {Object} - Orders data and utility functions
 */
export const useOrders = (options = {}) => {
  const {
    realtime = true,
    deliveryDate = dayjs().format(DATE_FORMAT_DD_MM_YYYY),
    refreshInterval = realtime ? 30000 : 0, // 30 seconds for realtime, no refresh otherwise
  } = options;

  console.log("[useOrders] 📅 Hook initialized with deliveryDate:", deliveryDate);
  console.log("[useOrders] 🔄 Realtime mode:", realtime);
  console.log("[useOrders] ⏱️  Refresh interval:", refreshInterval);

  // Use SWR for data fetching with caching
  const {
    data: orders,
    error,
    isLoading,
    mutate,
  } = useSWR([`orders`, deliveryDate], ([_, date]) => fetchOrdersFromFirestore(date), {
    refreshInterval,
    revalidateOnFocus: realtime,
    revalidateOnReconnect: realtime,
    dedupingInterval: 5000, // Dedupe requests within 5 seconds
  });

  /**
   * Manually refresh orders
   * @returns {Promise<Array>} - Updated orders
   */
  const refreshOrders = useCallback(() => {
    return mutate();
  }, [mutate]);

  /**
   * Find a specific order by ID in both databases
   * @param {string} orderId - The order ID to search for
   * @returns {Promise<Object|null>} - The order object or null if not found
   */
  const findOrderById = useCallback(async (orderId) => {
    if (!orderId) {
      console.warn("useOrders.findOrderById: No orderId provided");
      return null;
    }

    try {
      // Search in VCE first
      let snapshot = await vceFirestore
        .collection(FIRESTORE_ORDERS_TABLE)
        .where("orderId", "==", orderId)
        .limit(1)
        .get();

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          shopSource: "VCE",
          shopName: "Vleescentrum",
        };
      }

      // Search in HVO
      snapshot = await hvoFirestore.collection(FIRESTORE_ORDERS_TABLE).where("orderId", "==", orderId).limit(1).get();

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          shopSource: "HVO",
          shopName: "Halalvlees Online",
        };
      }

      return null;
    } catch (err) {
      console.error("Error finding order by ID:", err);
      return null;
    }
  }, []);

  /**
   * Update an order in the appropriate database with optimistic updates
   * @param {Object} order - The order object with shopSource property
   * @param {Object} updates - The fields to update
   * @returns {Promise<boolean>} - Success status
   */
  const updateOrder = useCallback(
    async (order, updates) => {
      if (!order || !order.id || !order.shopSource) {
        console.error("useOrders.updateOrder: Invalid order object");
        return false;
      }

      try {
        const firestore = order.shopSource === "VCE" ? vceFirestore : hvoFirestore;

        // Optimistically update the UI
        await mutate(
          async (currentOrders) => {
            // Update Firestore
            await firestore.collection(FIRESTORE_ORDERS_TABLE).doc(order.id).update(updates);

            // Return updated orders for cache
            return currentOrders?.map((o) =>
              o.id === order.id && o.shopSource === order.shopSource ? { ...o, ...updates } : o,
            );
          },
          {
            optimisticData: orders?.map((o) =>
              o.id === order.id && o.shopSource === order.shopSource ? { ...o, ...updates } : o,
            ),
            rollbackOnError: true,
            revalidate: false, // Don't revalidate immediately, update will show instantly
          },
        );

        return true;
      } catch (err) {
        console.error("Error updating order:", err);
        return false;
      }
    },
    [mutate, orders],
  );

  /**
   * Get orders filtered by status
   * @param {string} status - Order status to filter by
   * @returns {Array} - Filtered orders
   */
  const getOrdersByStatus = useCallback(
    (status) => {
      if (!status || status === "all") return orders;
      return orders.filter((order) => order.orderStatus === status);
    },
    [orders],
  );

  /**
   * Get orders filtered by shop source
   * @param {string} shopSource - "VCE" or "HVO"
   * @returns {Array} - Filtered orders
   */
  const getOrdersByShop = useCallback(
    (shopSource) => {
      if (!shopSource) return orders;
      return orders.filter((order) => order.shopSource === shopSource);
    },
    [orders],
  );

  return {
    orders: orders || [],
    isLoading,
    error,
    refreshOrders,
    findOrderById,
    updateOrder,
    getOrdersByStatus,
    getOrdersByShop,
  };
};
